import { useGetUrl } from '@seek/ca-myprofile-utils';
import { createForm, type RenderParams } from '@seek/forms-ui';
import { useDialogHistoryManager } from '@seek/profile-base-ui';
import {
  PhoneNumberCountryCallingCode,
  ProfileAutosuggest,
} from '@seek/profile-edit-ui';
import { CountryCallingCodeDialog } from '@seek/profile-read-ui';
import { rules } from '@seek/validators-js';
import { useTranslations } from '@vocab/react';
import {
  Text,
  Stack,
  Button,
  Actions,
  Tiles,
  TextField,
  Alert,
  TextLink,
  Box,
} from 'braid-design-system';
import { useCallback, useEffect, useState } from 'react';

import { onSubmitFail } from 'src/components/Card/onSubmitFail';
import { ExternalLink } from 'src/components/ExternalLink/ExternalLink';
import type { DirtyFormProps } from 'src/components/FormDrawer/FormDrawer';
import { useOnFormDirtyChange } from 'src/components/FormDrawer/useOnFormDirtyChange';
import { useEnterReadMode } from 'src/components/Router/useEnterReadMode';
import { usePage } from 'src/components/Router/usePage';
import { PERSONAL_DETAILS_FORM } from 'src/context/FormContext';
import { useAppConfig } from 'src/modules/appConfig';
import { useFocus } from 'src/modules/hooks/useFocus';

import { usePersonalDetails, useUpdatePersonalDetails } from '../data';
import type {
  CurrentLocation2,
  CurrentLocation2AutosuggestData,
  PersonalDetails,
  PersonalDetailsSection,
} from '../types';

import translations from './.vocab';
import { getInitialValues, getStructuredData } from './utils';

export type FormValues = Pick<
  PersonalDetails,
  'firstName' | 'lastName' | 'phoneNumber'
> & {
  countryCallingCode: { id: string };
  currentLocation2?: CurrentLocation2AutosuggestData;
  nationality: null;
};

const mapFormStateToProps = ({
  dirty: mapIsValueDirty,
  form: { dirty: isFormDirty },
}: {
  dirty: Record<keyof FormValues, boolean>;
  form: {
    dirty: boolean;
  };
}) => ({
  mapIsValueDirty,
  isFormDirty,
});

type MapFormStateToProps = ReturnType<typeof mapFormStateToProps>;

type FormProps = RenderParams<FormValues> & MapFormStateToProps;

const { FormProvider, useField } = createForm<FormValues, MapFormStateToProps>(
  PERSONAL_DETAILS_FORM,
  {
    formType: 'braid',
  },
);

const nameValidationRules = rules.name();
const phoneNumberRules = rules.phoneNumber();

const EditPersonalDetailsForm = ({
  mapIsValueDirty,
  isFormDirty,
  handleSubmit,
  onFormDirtyChange,
}: FormProps & DirtyFormProps) => {
  const { zone, locale, brand, language, isUnifiedSite } = useAppConfig();
  const { personalDetails } = usePersonalDetails();
  const [updatedPersonalDetailsSections, setUpdatedPersonalDetailsSections] =
    useState<string[]>([]);

  useEffect(() => {
    const section: Record<PersonalDetailsSection, boolean> = {
      'first name': mapIsValueDirty.firstName,
      'last name': mapIsValueDirty.lastName,
      'home location': mapIsValueDirty.currentLocation2,
      'phone number':
        mapIsValueDirty.countryCallingCode || mapIsValueDirty.phoneNumber,
    };
    const sectionKeys: PersonalDetailsSection[] = Object.keys(section);
    const dirtySections = sectionKeys.filter((key) => section[key] === true);

    setUpdatedPersonalDetailsSections(dirtySections);
  }, [mapIsValueDirty]);

  const emailAddress = personalDetails?.emailAddress;
  const countryCallingCodeValue =
    personalDetails?.personalDetails.countryCallingCode;
  const phoneNumberValue = personalDetails?.personalDetails.phoneNumber;
  const hasSavedCountryCallingCode = Boolean(countryCallingCodeValue);

  const [dialogOpen, setDialogOpen] = useState(false);
  const { openDialog, closeDialog } = useDialogHistoryManager(setDialogOpen);

  const getUrl = useGetUrl(zone, language);
  const { t } = useTranslations(translations);
  const enterReadMode = useEnterReadMode();

  useOnFormDirtyChange({ onFormDirtyChange, dirty: isFormDirty });

  const firstNameRef = useFocus<HTMLInputElement>({
    card: 'personal-details',
    fieldName: 'first-name',
  });
  const lastNameRef = useFocus<HTMLInputElement>({
    card: 'personal-details',
    fieldName: 'last-name',
  });
  const phoneNumberRef = useFocus<HTMLInputElement>({
    card: 'personal-details',
    fieldName: 'phone-number',
  });
  const currentLocation2Ref = useFocus<HTMLInputElement>({
    card: 'personal-details',
    fieldName: 'current-location2',
  });

  const firstNameField = useField({
    id: 'firstName',
    ...nameValidationRules,
  });

  const lastNameField = useField({
    id: 'lastName',
    ...nameValidationRules,
  });

  const phoneNumberField = useField({
    id: 'phoneNumber',
    ...phoneNumberRules,
  });

  const countryCallingCodeField = useField({
    id: 'countryCallingCode',
  });

  const currentLocationField2 = useField({
    id: 'currentLocation2',
    ...rules.granularCurrentLocation({
      dataLabel: t('location'),
    }),
  });

  const showCountryCallingCodeMessaging =
    !hasSavedCountryCallingCode &&
    Boolean(phoneNumberValue) &&
    brand === 'SEEK';

  const [updatePersonalDetails] = useUpdatePersonalDetails();

  const submitForm = useCallback(
    ({
      currentLocation2: newCurrentLocation2,
      ...personalDetailsData
    }: FormValues) => {
      const showCountryCallingCodeDialog =
        showCountryCallingCodeMessaging && Boolean(phoneNumberField.value);

      const currentLocation2Data = {
        currentLocation: {
          id: Number(newCurrentLocation2?.value || 0),
        },
      };

      if (showCountryCallingCodeDialog && !dialogOpen) {
        openDialog();
      } else {
        if (
          isFormDirty ||
          (!hasSavedCountryCallingCode && Boolean(phoneNumberValue))
        ) {
          updatePersonalDetails(
            {
              personalDetailsData,
              currentLocation2Data,
              countryCode: locale,
              languageCode: language,
              zone,
            },
            {
              updatedCurrentLocation2: newCurrentLocation2,
              updatedPersonalDetailsSections,
            },
          );
        }
        enterReadMode();
        setDialogOpen(false);
      }
    },
    [
      showCountryCallingCodeMessaging,
      phoneNumberField.value,
      dialogOpen,
      openDialog,
      isFormDirty,
      hasSavedCountryCallingCode,
      updatePersonalDetails,
      zone,
      locale,
      language,
      updatedPersonalDetailsSections,
      phoneNumberValue,
      enterReadMode,
    ],
  );

  const onClose = useCallback(() => {
    closeDialog();
  }, [closeDialog]);

  return (
    <form onSubmit={handleSubmit(submitForm)}>
      <Stack space="large">
        {showCountryCallingCodeMessaging && (
          <Alert tone="info">
            <Text>
              Phone number has been updated to include a country code.
            </Text>
          </Alert>
        )}
        <Stack space="small">
          <Tiles space={['small', 'medium']} columns={[1, 2]}>
            <TextField
              id="firstName"
              label={t('First name')}
              ref={firstNameRef}
              data={{ automation: 'first-name', 'hj-masked': 'true' }}
              reserveMessageSpace
              {...firstNameField}
            />
            <TextField
              id="lastName"
              label={t('Last name')}
              data={{
                'hj-masked': 'true',
                automation: 'last-name',
              }}
              ref={lastNameRef}
              reserveMessageSpace
              {...lastNameField}
            />
          </Tiles>
          <ProfileAutosuggest
            label={t('Home location')}
            id="currentLocation2"
            asset="ProfileLocation"
            profileLocationProps={{
              zone,
              locationType: 'Home',
            }}
            showNoResultsMessage={true}
            inputRef={currentLocation2Ref}
            data={{ automation: 'current-location2' }}
            getStructuredData={getStructuredData}
            {...currentLocationField2}
            value={currentLocationField2.value || { text: '' }}
          />
          <PhoneNumberCountryCallingCode
            ref={phoneNumberRef}
            phoneNumber={phoneNumberField.value}
            onPhoneNumberChange={(value) => phoneNumberField.onChange(value)}
            countryCallingCode={countryCallingCodeField.value}
            onCountryCallingCodeChange={countryCallingCodeField.onChange}
            secondaryLabel={t('recommended')}
            message={phoneNumberField.errorMessage}
            {...(!phoneNumberField.valid ? { tone: 'critical' as const } : {})}
          />
          <Stack space="medium">
            <Text component="h3" weight="strong">
              {t('Email address')}
            </Text>
            {isUnifiedSite ? (
              <Box display="flex" justifyContent="spaceBetween">
                <Text data={{ 'hj-masked': 'true' }}>{emailAddress}</Text>
                <Text>
                  <TextLink
                    target="_blank"
                    rel="noopener noreferrer"
                    href={getUrl('settings')}
                  >
                    {t('Edit in Settings')}
                  </TextLink>
                </Text>
              </Box>
            ) : (
              <>
                <Text data={{ 'hj-masked': 'true' }}>{emailAddress}</Text>
                <Alert tone="info">
                  <Text>
                    {t('Change your account email and password in Settings.', {
                      SettingsLink: (v) => (
                        <ExternalLink
                          target="_blank"
                          rel="noopener noreferrer"
                          href={getUrl('settings')}
                        >
                          {v}
                        </ExternalLink>
                      ),
                    })}
                  </Text>
                </Alert>
              </>
            )}
          </Stack>
        </Stack>
        <Actions>
          <Button
            type="submit"
            tone="formAccent"
            data={{ automation: 'personal-details-save-button' }}
          >
            {t('Save')}
          </Button>
          <Button
            tone="formAccent"
            data={{ automation: 'personal-details-cancel-button' }}
            onClick={enterReadMode}
            variant="transparent"
          >
            {t('Cancel')}
          </Button>
        </Actions>
      </Stack>
      <CountryCallingCodeDialog
        phoneNumber={phoneNumberField.value || ''}
        countryCallingCode={{ id: countryCallingCodeField.value.id }}
        open={dialogOpen}
        onSave={handleSubmit(submitForm)}
        onClose={onClose}
      />
    </form>
  );
};

export const EditPersonalDetails = (dirtyFormProps: DirtyFormProps) => {
  const { personalDetails, loading } = usePersonalDetails();
  const personalDetailsData = {
    firstName: personalDetails?.personalDetails.firstName || '',
    lastName: personalDetails?.personalDetails.lastName || '',
    currentLocation2:
      (personalDetails?.currentLocation2 as CurrentLocation2) || {},
    emailAddress: personalDetails?.emailAddress,
    phoneNumber: personalDetails?.personalDetails.phoneNumber || '',
    countryCallingCode:
      personalDetails?.personalDetails.countryCallingCode || null,
    defaultCountryCallingCode: personalDetails?.personalDetails
      .defaultCountryCallingCode || { id: '1' },
  };
  const page = usePage();
  const { language } = useAppConfig();
  const onSubmitFailFn = (errors: string[]) => {
    onSubmitFail(errors, page);
  };

  if (loading) {
    return null;
  }

  return (
    <FormProvider
      mapFormStateToProps={mapFormStateToProps}
      initialValues={getInitialValues(personalDetailsData)}
      onSubmitFail={onSubmitFailFn}
      language={language}
    >
      {(formProps) => (
        <EditPersonalDetailsForm {...dirtyFormProps} {...formProps} />
      )}
    </FormProvider>
  );
};
